// BrandCrumb — the one brand lockup + breadcrumb.
//
// ⚠️ THERE IS NO BREADCRUMB ON MOBILE. Every segment carries `.crumb-seg`, and
// that class is hidden outright below 720px (globals.css). The trail is a
// desktop affordance: on a phone it competes with the ≡ and the you-chip for a
// row that is already too narrow, and the moment a title or a competitor name
// lands in it the lockup runs under the chip — which is exactly what it was
// doing. The ≡ menu carries navigation at that width (it already holds the
// whole site map), so a phone gets the wordmark and the ≡ and nothing else.
//
// Hiding it in CSS rather than in JS is deliberate: BrandCrumb has no "use
// client" and is mounted from server components, a hook here would make every
// one of them client-rendered, and a JS width check would flash the trail on
// first paint before it measured.
//
// The root is the innernet
// WORDMARK (the real letterform), baseline-aligned and em-sized so it reads as the
// exact same size/height as the directory crumbs beside it — one continuous thing.
// "[innernet] / a / b / c": wordmark in ink, faded "/" separators, EVERY crumb
// segment in full ink — only the search affordance ever reads faded/placeholder.
// One font-size drives the whole lockup.

import Link from "next/link";
import Wordmark, { WORDMARK_INLINE_EM } from "./brand/Wordmark";
import OrganismMark from "./brand/OrganismMark";

// The wordmark's letterform x-height as a fraction of its rendered height; sizing
// the mark to ~this of an em lands its letters on the crumb text's letter-height.
// Shared with every other inline (baseline-aligned) use of the wordmark — see
// WORDMARK_INLINE_EM in brand/Wordmark.tsx.
const WORDMARK_EM = WORDMARK_INLINE_EM;

export interface Crumb {
  label: string;
  href?: string;
  onClick?: () => void;
  /**
   * Let this segment ellipsize instead of pushing the crumb off the screen.
   *
   * Set it on any segment whose text is authored rather than structural — a post
   * title, a skill name, an agent-named artifact. A trail of directory names is
   * bounded; a title is not, and one long one runs the whole lockup past the
   * viewport on a phone and into the avatar on a desktop.
   *
   * Note the truncation lives on the TEXT span, never the flex wrapper:
   * `overflow: hidden` on the wrapper crops the wordmark's leaf-flick and the
   * i-dot (law_no_clipping), which is why several call sites deliberately pass
   * `overflow: visible`.
   */
  truncate?: boolean;
}

export default function BrandCrumb({
  segments = [],
  size = "var(--t-crumb)",
  className,
  style,
  rootHref = "/",
  onRoot,
  compact = false,
  collapse = false,
  afterRoot,
}: {
  segments?: Crumb[];
  size?: string;
  /** @deprecated kept for call-site compatibility; the wordmark is text now. */
  wordmarkWidth?: number;
  className?: string;
  style?: React.CSSProperties;
  /** Where the "innernet" root links to (default the marketing home). */
  rootHref?: string;
  /** If set, the root becomes a button calling this instead of a link — used to
   *  reset the OS shell to the dashboard root from any depth without leaving. */
  onRoot?: () => void;
  /** the root shows the compact ORGANISM ICON instead of the wordmark (mobile /
   *  when it would clash with the avatar). */
  compact?: boolean;
  /** drop the middle crumbs, keeping just the current (deepest) directory. */
  collapse?: boolean;
  /** optional element rendered right AFTER the "innernet" root, before the first
   *  crumb — e.g. the dashboard menu button, so it reads "innernet ≡ / dir". */
  afterRoot?: React.ReactNode;
}) {
  // when collapsed, keep only the deepest crumb (the "last parent directory") and
  // hint the elision with a muted "…".
  const shown = collapse && segments.length > 1 ? segments.slice(-1) : segments;
  const elided = collapse && segments.length > 1;
  // the compact icon is baseline-aligned like the wordmark, but the organism mark's
  // optical centre sits high — nudge it down so it rests on the crumb text baseline.
  const Root = compact ? (
    <span style={{ display: "inline-flex", transform: "translateY(0.16em)" }}><OrganismMark size="1.02em" /></span>
  ) : (
    <Wordmark height={WORDMARK_EM} />
  );
  const text = (label: string, color: string, truncate?: boolean) => (
    <span
      title={truncate ? label : undefined}
      style={{
        color,
        whiteSpace: "nowrap",
        ...(truncate
          ? {
              display: "inline-block",
              verticalAlign: "bottom",
              maxWidth: "min(28ch, 42vw)",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }
          : {}),
      }}
    >
      {label}
    </span>
  );
  return (
    <div
      className={className}
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: "0.5rem",
        minWidth: 0,
        fontSize: size,
        // a tighter-than-body but descender-safe line box: lineHeight:1 was
        // cropping g/y/p tails (and any overflow:hidden wrapper made it worse).
        lineHeight: 1.3,
        paddingBottom: "0.08em",
        letterSpacing: "-0.03em",
        ...style,
      }}
    >
      {onRoot ? (
        <button
          onClick={onRoot}
          aria-label="innernet dashboard"
          style={{ color: "var(--ink)", flexShrink: 0, display: "inline-flex", alignItems: "baseline", background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit", letterSpacing: "inherit", lineHeight: "inherit" }}
        >
          {Root}
        </button>
      ) : (
        <Link href={rootHref} aria-label="innernet home" style={{ color: "var(--ink)", flexShrink: 0, display: "inline-flex", alignItems: "baseline" }}>
          {Root}
        </Link>
      )}
      {afterRoot}
      {elided && (
        <span className="crumb-seg" style={{ display: "inline-flex", alignItems: "baseline", gap: "0.5rem", flexShrink: 0 }}>
          <span aria-hidden style={{ color: "var(--ink-4)" }}>/</span>
          <span aria-hidden style={{ color: "var(--ink-4)" }}>…</span>
        </span>
      )}
      {shown.map((s, i) => {
        const body = text(s.label, "var(--ink)", s.truncate);
        return (
          <span key={i} className="crumb-seg" style={{ display: "inline-flex", alignItems: "baseline", gap: "0.5rem", minWidth: 0 }}>
            <span aria-hidden style={{ color: "var(--ink-4)" }}>/</span>
            {s.href ? (
              <Link href={s.href} style={{ display: "inline-flex" }}>{body}</Link>
            ) : s.onClick ? (
              <button onClick={s.onClick} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit", letterSpacing: "inherit" }}>
                {body}
              </button>
            ) : (
              body
            )}
          </span>
        );
      })}
    </div>
  );
}
